import React from "react";
import { Box, Typography, Button, Paper, Avatar, Container } from "@mui/material";
import NameLogo from "src/@core/components/icons/Name-Logo";
import EmailCheckIcon from "src/@core/components/icons/EmailCheckIcon";

import OtpVerificationDialog from "src/@core/components/OtpVerificationDialog";
import { useMutation, useQueryClient } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { fetchUser } from "src/redux/actions/AuthAction";
import authConfig from "../../configs/auth";
import { setIsShowSetupMenu } from "src/redux/slices/AuthSlice";
import { useNavigate } from "react-router-dom";


const useReSendOtp = () => {
    return useMutation({
        mutationFn: async (businessId) => {
            const res = await axiosInstance.get(`${ApiEndPoints.BUSINESS.resendOtp}/${businessId}`)
            return res.data; // array of usernames
        }
    });
};

const useVerifyOtp = () => {
    return useMutation({
        mutationFn: async (data) => {
            const res = await axiosInstance.post(`${ApiEndPoints.BUSINESS.verifyOtp}/${data.businessId}`, {
                otp: data.otp
            })
            return res.data; // array of usernames
        }
    });
};


const EmailIcon = () => (
    <Box
        sx={{
            width: { xs: 80, sm: 100 },
            height: { xs: 80, sm: 100 },
            bgcolor: '#F3F0FF',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            mx: 'auto',
            '&::before': {
                content: '""',
                position: 'absolute',
                width: '100%',
                height: '100%',
                borderRadius: '50%',
                border: '3px solid #A259FF',
                opacity: 0.3,
                animation: 'pulse 2s infinite'
            }
        }}
    >
        <Box
            sx={{
                width: { xs: 40, sm: 50 },
                height: { xs: 40, sm: 50 },
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: { xs: '1.5rem', sm: '2rem' }
            }}
        >
            <EmailCheckIcon />
        </Box>
    </Box>
);

const EmailVerification = ({
    isMaxView = false,
    openVerifyDialog,
    setOpenVerifyDialog
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const queryClient = useQueryClient(); // Add this hook
    const { mutate: reSendOtp } = useReSendOtp();
    const { mutate: verifyOtp } = useVerifyOtp();
    const selectedBusinessId = useSelector((state) => state.auth.selectedBusinessId);

    const sendVerificationEmail = () => {
        if (!selectedBusinessId) {
            toast.error('Business ID not found. Please try again later.');
            return;
        }

        reSendOtp(selectedBusinessId, {
            onSuccess: (data) => {
                toast.success('OTP resent  !');
                setOpenVerifyDialog(true)
            },
            onError: (error) => {
                console.log('reSendOtp failed:', error.response.data.message);
                toast.error(error?.response?.data?.message || 'something went wrong');
            }
        });
    }

       const onVerify = (otpString) => {
        let obj = {
            otp: otpString,
            businessId: selectedBusinessId
        };
        verifyOtp(obj, {
            onSuccess: async (data) => {
                toast.success('OTP verified  !');
                setOpenVerifyDialog(false);

                // 1. Get token from response or localStorage
                const storedToken = data?.token ||
                    window.localStorage.getItem(authConfig.storageTokenKeyName);

                // 2. Call fetchUser with the token
                const resultAction = await dispatch(fetchUser({ storedToken }));

                // 3. If fetchUser succeeded, refetch businessLocationData, close dialog and navigate
                if (fetchUser.fulfilled.match(resultAction)) {
                    // Refetch businessLocationData
                    await queryClient.refetchQueries(["businessLocationData", selectedBusinessId]);
                    
                    // Close the setup dialog
                    dispatch(setIsShowSetupMenu(false));
                    
                    // Navigate to dashboard
                    navigate('/dashboard');
                }
            },
            onError: (error) => {
                toast.error(error?.response?.data?.message || 'otp verify failed. Please try again.');
            }
        });
    };


    return (
        <Container maxWidth="sm" sx={{ minHeight: '100%', display: 'flex', alignItems: 'center', py: 4 }}>
            <Paper
                elevation={'none'}
                sx={{
                    width: '100%',
                    maxWidth: 420,
                    mx: "auto",
                    py: { xs: 4, sm: 6 },
                    px: { xs: 3, sm: 4 },
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    borderRadius: { xs: 3, sm: 4 },
                    minHeight: { xs: 500, sm: 560 },
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: 150,
                        height: 150,

                        borderRadius: '50%',
                        transform: 'translate(50px, -50px)'
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        width: 100,
                        height: 100,

                        borderRadius: '50%',
                        transform: 'translate(-30px, 30px)'
                    }
                }}
            >
                {/* Logo Avatar */}
                <Avatar
                    sx={{
                        width: { xs: 80, sm: 90 },
                        height: { xs: 80, sm: 90 },
                        bgcolor: "#fff",
                        border: "4px solid #A259FF",
                        mx: "auto",
                        mb: 2,
                        p: { xs: 4, sm: 5 },
                        boxShadow: '0 8px 25px rgba(162, 89, 255, 0.25)',
                        position: 'relative',
                        zIndex: 1
                    }}
                >
                    <NameLogo />
                </Avatar>

                {/* Brand Title */}
                <Typography
                    variant="h3"
                    align="center"
                    fontWeight="bold"
                    letterSpacing={1}
                    sx={{
                        mb: 0.5,
                        textTransform: "lowercase",
                        color: "#000",
                        fontFamily: "Inter, Arial, sans-serif",
                        fontSize: { xs: '2rem', sm: '2.5rem' },
                        position: 'relative',
                        zIndex: 1
                    }}
                >
                    beɘ
                    <Box component="span" sx={{ color: "#A259FF" }}>
                        m
                    </Box>
                    z
                </Typography>

                <Typography
                    variant="subtitle2"
                    sx={{
                        color: "#666",
                        fontWeight: 500,
                        fontSize: { xs: 13, sm: 14 },
                        mb: { xs: 4, sm: 5 },
                        textAlign: "center",
                        letterSpacing: 0.5,
                        position: 'relative',
                        zIndex: 1
                    }}
                >
                    business
                </Typography>

                {/* Main Message */}
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: { xs: 14, sm: 16 },
                        textAlign: "center",
                        mb: { xs: 4, sm: 5 },
                        color: '#333',
                        lineHeight: 1.5,
                        position: 'relative',
                        zIndex: 1
                    }}
                >
                    in order to continue you need to <br />
                    confirm your email address
                </Typography>

                {/* Email Icon */}
                <Box sx={{ mb: { xs: 4, sm: 5 }, position: 'relative', zIndex: 1 }}>
                    <EmailIcon />
                </Box>

                {/* Description */}
                <Typography
                    sx={{
                        mb: { xs: 3, sm: 4 },
                        color: "#555",
                        fontSize: { xs: 14, sm: 15 },
                        opacity: 0.9,
                        textAlign: "center",
                        lineHeight: 1.6,
                        position: 'relative',
                        zIndex: 1
                    }}
                >
                    we will send you an email to <br />
                    verify your account
                </Typography>

                {/* Send Button */}
                <Button
                    variant="contained"
                    fullWidth
                    onClick={sendVerificationEmail}
                    sx={{
                        mt: 1,
                        bgcolor: "#7C3AED",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: { xs: 16, sm: 18 },
                        borderRadius: 2.5,
                        boxShadow: "0px 4px 15px rgba(124, 58, 237, 0.4)",
                        textTransform: "lowercase",
                        py: { xs: 1.2, sm: 1.4 },
                        maxWidth: { xs: 250, sm: 270 },
                        position: 'relative',
                        zIndex: 1,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            bgcolor: "#6366F1",
                            transform: 'translateY(-2px)',
                            boxShadow: "0px 6px 20px rgba(124, 58, 237, 0.5)"
                        },
                        '&:active': {
                            transform: 'translateY(0px)'
                        }
                    }}
                >
                    send Verification Email
                </Button>
            </Paper>

            <OtpVerificationDialog open={openVerifyDialog}
                isMaxView={isMaxView}
                onClose={() => setOpenVerifyDialog(false)}
                onResend={sendVerificationEmail}
                onVerify={onVerify}
            />
            {/* Global styles for animations */}
            <style jsx global>{`
      @keyframes pulse {
        0% {
          transform: scale(1);
          opacity: 0.3;
        }
        50% {
          transform: scale(1.1);
          opacity: 0.1;
        }
        100% {
          transform: scale(1);
          opacity: 0.3;
        }
      }
    `}</style>
        </Container>
    )
};

export default EmailVerification;